import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import Swal from 'sweetalert2';
import { useAuth } from './context/AuthContext';
import LockOverlay from './components/LockOverlay';
import { fm } from './utils';
import './KasaPanel.css';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const formatDate = (tarih) => {
  if (!tarih) return '';
  const d = new Date(tarih);
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
};

const formatSaat = (tarih) => {
  if (!tarih) return '--:--';
  return new Date(tarih).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', hour12: false });
};

const sureDk = (tarih) => {
  if (!tarih) return 999;
  return Math.floor((Date.now() - new Date(tarih).getTime()) / 60000);
};

const sureFarki = (tarih) => {
  if (!tarih) return null;
  const sn = Math.floor((Date.now() - new Date(tarih).getTime()) / 1000);
  if (sn < 0) return '00:00';
  const dk = Math.floor(sn / 60);
  const ss = Math.floor(dk / 60);
  return `${String(ss).padStart(2, '0')}:${String(dk % 60).padStart(2, '0')}`;
};

const kartRenk = (sonTarih) => {
  const dk = sureDk(sonTarih);
  if (dk < 15) return {
    accent: 'border-l-emerald-500',
    bg: 'bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-900/15 dark:to-surface-800',
    border: 'border-emerald-200/50 dark:border-emerald-800/30',
    badge: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300',
    dot: 'bg-emerald-500',
    text: 'text-emerald-600 dark:text-emerald-400',
    shadow: 'shadow-emerald-500/5',
    pulse: false
  };
  if (dk < 30) return {
    accent: 'border-l-amber-500',
    bg: 'bg-gradient-to-br from-amber-50 to-white dark:from-amber-900/15 dark:to-surface-800',
    border: 'border-amber-200/50 dark:border-amber-800/30',
    badge: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
    dot: 'bg-amber-500',
    text: 'text-amber-600 dark:text-amber-400',
    shadow: 'shadow-amber-500/5',
    pulse: false
  };
  return {
    accent: 'border-l-red-500',
    bg: 'bg-gradient-to-br from-red-50 to-white dark:from-red-900/15 dark:to-surface-800',
    border: 'border-red-200/50 dark:border-red-800/30',
    badge: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
    dot: 'bg-red-500',
    text: 'text-red-600 dark:text-red-400',
    shadow: 'shadow-red-500/5',
    pulse: true
  };
};

const odenenKartRenk = {
  accent: 'border-l-emerald-500',
  bg: 'bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-900/15 dark:to-surface-800',
  border: 'border-emerald-200/50 dark:border-emerald-800/30',
  badge: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300',
  dot: 'bg-emerald-500',
  text: 'text-emerald-600 dark:text-emerald-400',
  shadow: 'shadow-emerald-500/5',
  pulse: false
};

const OzetCard = ({ label, value, color = 'emerald', icon }) => (
  <div className={`bg-${color}-50 dark:bg-${color}-900/10 rounded-2xl p-4 border border-${color}-100 dark:border-${color}-800/20`}>
    <div className="flex items-center gap-2 mb-1.5">
      {icon && <span className="text-lg">{icon}</span>}
      <span className="text-[10px] font-medium text-surface-400 dark:text-surface-500 uppercase tracking-wider">{label}</span>
    </div>
    <span className={`font-display font-bold text-xl text-${color}-600 dark:text-${color}-400`}>{value}</span>
  </div>
);

const MasaKartKasa = ({ masaKart, detayAc }) => {
  const renk = masaKart.odendi ? odenenKartRenk : kartRenk(masaKart.sonSiparisTarihi);
  const sure = sureFarki(masaKart.sonSiparisTarihi);

  return (
    <div className={`relative rounded-2xl border ${renk.border} ${renk.bg} ${renk.shadow} shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 overflow-hidden group ${renk.pulse && !masaKart.odendi ? 'animate-pulse-soft' : ''}`}>
      <div className={`absolute left-0 top-0 bottom-0 w-1.5 ${renk.accent} rounded-l-2xl`} />
      <div className="p-4 pl-5">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="font-bold text-surface-800 dark:text-white text-lg">{masaKart.masaNumarasi}</h3>
            {masaKart.konum && <p className="text-[11px] text-surface-400 flex items-center gap-1 mt-0.5">
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
              {masaKart.konum}
            </p>}
          </div>
          {!masaKart.odendi && (
            <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold tabular-nums ${renk.badge}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${renk.dot}`} />
              {sure}
            </div>
          )}
          {masaKart.odendi && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              Ödendi
            </div>
          )}
        </div>

        <div className="space-y-2 text-xs">
          <div className="flex items-center justify-between">
            <span className="text-surface-400 flex items-center gap-1">
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
              Garson
            </span>
            <span className="font-semibold text-surface-700 dark:text-surface-200">{masaKart.garsonAdi || '?'}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-surface-400">
              <svg className="w-3 h-3 inline mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              İlk Sipariş
            </span>
            <span className="font-medium text-surface-700 dark:text-surface-200">{formatSaat(masaKart.ilkSiparisTarihi)}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-surface-400">
              <svg className="w-3 h-3 inline mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              Son Sipariş
            </span>
            <span className="font-medium text-surface-700 dark:text-surface-200">{formatSaat(masaKart.sonSiparisTarihi)}</span>
          </div>

          <div className="border-t border-surface-100 dark:border-surface-700/50 my-2" />

          <div className="flex items-center justify-between">
            <span className="text-surface-400">
              <svg className="w-3 h-3 inline mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
              Kalem
            </span>
            <span className="font-semibold text-surface-800 dark:text-white">{masaKart.toplamKalem} ürün</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-surface-400">
              <svg className="w-3 h-3 inline mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              Tutar
            </span>
            <span className="font-bold text-emerald-600 dark:text-emerald-400">{fm(masaKart.toplamTutar)}</span>
          </div>
        </div>
      </div>

      <div className="px-4 pb-4 pl-5">
        <button onClick={() => detayAc(masaKart)}
          className="w-full py-2.5 text-xs font-semibold bg-white/80 dark:bg-surface-700/80 backdrop-blur-sm border border-surface-200 dark:border-surface-600 text-surface-600 dark:text-surface-300 rounded-xl hover:bg-white dark:hover:bg-surface-600 hover:border-surface-300 dark:hover:border-surface-500 transition-all active:scale-[0.97] opacity-80 group-hover:opacity-100">
          <svg className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg>
          {masaKart.odendi ? 'Fişleri Görüntüle' : 'Sipariş Detayı'}
        </button>
      </div>
    </div>
  );
};

const MasaDetayModal = ({ masaKart, seciliDetay, onClose, odemeler, kullanici, hesaplaToplam, masaKalanTutar, urunIptalEt, modalAc, modalAlmanUsuluAc, fisYazdir }) => {
  const sonSiparis = masaKart?.sonSiparis;
  const masaOdemeleri = odemeler.filter(o => masaKart?.siparisler?.some(s => s.siparisID === o.siparisID));
  const odenenToplam = masaOdemeleri.reduce((t, o) => t + (o.toplamTutar || o.odemeTutari || 0), 0);

  if (!masaKart) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div className="relative w-full max-w-lg bg-white dark:bg-surface-800 rounded-2xl shadow-2xl animate-scale-up max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white dark:bg-surface-800 border-b border-surface-100 dark:border-surface-700 px-5 py-3 flex items-center justify-between z-10">
          <h3 className="font-display font-bold text-surface-800 dark:text-white text-sm">
            {masaKart.masaNumarasi}
            <span className="text-surface-400 font-medium ml-2 text-xs">{masaKart.garsonAdi}</span>
          </h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-700 text-surface-400 transition-colors">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <div className="p-5 space-y-2.5 max-h-60 overflow-y-auto">
          {seciliDetay?.siparisDetaylari?.length > 0 ? seciliDetay.siparisDetaylari.map((d, idx) => (
            <div key={idx} className={`flex items-center justify-between p-2.5 rounded-xl ${d.durum === 'İptal' ? 'bg-red-50 dark:bg-red-900/10 line-through' : 'bg-surface-50 dark:bg-surface-700/30'}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-surface-800 dark:text-surface-200 text-xs">{d.adet}x</span>
                  <span className="text-surface-600 dark:text-surface-400 text-xs truncate">{d.urunAdi || '?'}</span>
                </div>
                {(d.modifiers || d.modifierItems) && (() => {
                  const mods = d.modifiers || (d.modifierItems ? JSON.parse(d.modifierItems) : []);
                  const groups = {};
                  mods.forEach(m => {
                    const gName = m.modifierGroupAdi || 'Diğer';
                    if (!groups[gName]) groups[gName] = [];
                    const qty = m.qty || 1;
                    groups[gName].push(qty > 1 ? `${qty}x ${m.modifierItemAdi}` : m.modifierItemAdi);
                  });
                  return (
                    <div className="text-[10px] text-surface-400 mt-0.5 space-y-0.5">
                      {Object.entries(groups).map(([gAdi, items]) => (
                        <div key={gAdi} className="flex items-start gap-1">
                          <span className="font-medium text-surface-500 flex-shrink-0">{gAdi}:</span>
                          <span>{items.join(', ')}</span>
                        </div>
                      ))}
                    </div>
                  );
                })()}
                {d.notlar && (
                  <p className="text-[10px] text-amber-500 mt-0.5">📝 {d.notlar}</p>
                )}
                <div className="flex items-center gap-3 mt-0.5">
                  <span className="text-[10px] text-surface-400">{fm(d.birimFiyat || d.fiyat || 0)} × {d.adet}</span>
                  {d.odenenAdet > 0 && <span className="text-[10px] text-emerald-500 font-medium">Ödenen: {d.odenenAdet}</span>}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-surface-700 dark:text-surface-300 text-xs">
                  {d.toplamFiyat ? fm(d.toplamFiyat) : fm((d.birimFiyat || d.fiyat || 0) * d.adet)}
                </span>
                {d.durum !== 'İptal' && !masaKart.odendi && (kullanici?.rol === 'Admin' || kullanici?.rol === 'Kasa') && (
                  <button onClick={() => urunIptalEt(d.detayID, sonSiparis?.siparisID)} className="p-1.5 rounded-lg bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors">
                    <svg className="w-3 h-3 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                )}
              </div>
            </div>
          )) : (
            <div className="text-center py-8 text-surface-400 text-sm">Ürün bilgisi yükleniyor...</div>
          )}
        </div>

        <div className="px-5 pb-4">
          {masaOdemeleri.length > 0 && (
            <div className="space-y-1.5 mb-3">
              <p className="text-[10px] font-semibold text-surface-400 uppercase tracking-wider">Ödemeler</p>
              {masaOdemeleri.map(o => (
                <div key={o.odemeID} className="flex items-center justify-between text-xs">
                  <span className="text-surface-500 dark:text-surface-400">{o.odemeTuru || o.odemeYontemi}</span>
                  <span className="font-medium text-surface-700 dark:text-surface-300">{fm(o.toplamTutar || o.odemeTutari || 0)}</span>
                </div>
              ))}
              <div className="flex items-center justify-between pt-1.5 border-t border-surface-100 dark:border-surface-700">
                <span className="text-xs font-semibold text-surface-500 dark:text-surface-400">Ödenen Toplam</span>
                <span className="font-display font-bold text-emerald-600 dark:text-emerald-400">{fm(odenenToplam)}</span>
              </div>
            </div>
          )}
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold text-surface-500 dark:text-surface-400">Kalan</span>
            <span className="font-display font-bold text-lg text-amber-600 dark:text-amber-400">{fm(masaKalanTutar(masaKart))}</span>
          </div>
        </div>

        {!masaKart.odendi && masaKalanTutar(masaKart) > 0 && (
          <div className="px-5 pb-4 flex gap-2">
            <button onClick={() => modalAc(sonSiparis)} className="flex-1 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold text-xs rounded-xl shadow-lg shadow-emerald-500/20 transition-all active:scale-[0.98]">
              Normal Ödeme
            </button>
            <button onClick={() => modalAlmanUsuluAc(sonSiparis)} className="flex-1 py-2.5 bg-cyan-500 hover:bg-cyan-600 text-white font-semibold text-xs rounded-xl shadow-lg shadow-cyan-500/20 transition-all active:scale-[0.98]">
              Alman Usulü
            </button>
            <button onClick={() => fisYazdir(sonSiparis, seciliDetay)} className="px-4 py-2.5 bg-surface-100 dark:bg-surface-700 hover:bg-surface-200 dark:hover:bg-surface-600 text-surface-500 dark:text-surface-400 font-medium text-xs rounded-xl transition-all">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" /></svg>
            </button>
          </div>
        )}
        {!masaKart.odendi && masaKalanTutar(masaKart) <= 0 && (
          <div className="px-5 pb-4">
            <button onClick={() => fisYazdir(sonSiparis, seciliDetay)} className="w-full py-2.5 bg-surface-100 dark:bg-surface-700 hover:bg-surface-200 dark:hover:bg-surface-600 text-surface-500 dark:text-surface-400 font-medium text-xs rounded-xl transition-all">
              <svg className="w-4 h-4 inline mr-1.5 -mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" /></svg>
              Fiş Yazdır
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const KasaPanel = () => {
  const { token, kullanici, logout } = useAuth();
  const navigate = useNavigate();
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const [urunler, setUrunler] = useState([]);
  const [odemeler, setOdemeler] = useState([]);
  const [gunlukOzet, setGunlukOzet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seciliMasaKart, setSeciliMasaKart] = useState(null);
  const [seciliDetay, setSeciliDetay] = useState(null);
  const [anaTab, setAnaTab] = useState('gunluk');
  const [siparisler, setSiparisler] = useState([]);
  const [gunlukAltTab, setGunlukAltTab] = useState('acik');
  const [tahsilatSiparisler, setTahsilatSiparisler] = useState([]);
  const [tahsilatOdemeler, setTahsilatOdemeler] = useState([]);
  const [tahsilatAltTab, setTahsilatAltTab] = useState('tumu');
  const [baslangicTarih, setBaslangicTarih] = useState('');
  const [bitisTarih, setBitisTarih] = useState('');
  const [selectedUrunId, setSelectedUrunId] = useState('');
  const [selectedKategoriId, setSelectedKategoriId] = useState('');
  const [tahsilatYukleniyor, setTahsilatYukleniyor] = useState(false);
  const tahsilatYuklendi = useRef(false);
  const [modalAcik, setModalAcik] = useState(false);
  const [modalSiparis, setModalSiparis] = useState(null);
  const [odemeTuru, setOdemeTuru] = useState('Nakit');
  const [tutarInput, setTutarInput] = useState('');
  const [almanUsuluAcik, setAlmanUsuluAcik] = useState(false);
  const [almanUsuluUrunler, setAlmanUsuluUrunler] = useState([]);

  const kategoriler = React.useMemo(() => {
    const map = {};
    urunler.forEach(u => {
      const kid = u.kategoriID;
      if (kid && !map[kid]) {
        map[kid] = { kategoriID: kid, kategoriAdi: u.kategori?.kategoriAdi || `Kategori ${kid}` };
      }
    });
    return Object.values(map).sort((a, b) => a.kategoriAdi.localeCompare(b.kategoriAdi));
  }, [urunler]);

  const hesaplaToplam = (siparis) => {
    if (siparis.netTutar > 0) return siparis.netTutar;
    if (siparis.toplamTutar > 0) return siparis.toplamTutar;
    if (!siparis.siparisDetaylari) return 0;
    return siparis.siparisDetaylari.reduce((t, d) => t + ((d.birimFiyat || d.fiyat || 0) * (d.adet || 1)), 0);
  };

  const kalanTutar = (siparis) => {
    const tutar = hesaplaToplam(siparis);
    const odenen = odemeler.filter(o => o.siparisID === siparis.siparisID)
      .reduce((t, o) => t + (o.toplamTutar || o.odemeTutari || 0), 0);
    return Math.max(0, tutar - odenen);
  };

  const masaKalanTutar = (masaKart) => {
    let toplam = 0;
    masaKart.siparisler.forEach(s => {
      const tutar = hesaplaToplam(s);
      const odenen = odemeler.filter(o => o.siparisID === s.siparisID)
        .reduce((t, o) => t + (o.toplamTutar || o.odemeTutari || 0), 0);
      toplam += Math.max(0, tutar - odenen);
    });
    return toplam;
  };

  const getTodayStr = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const toDateStr = (dateStr) => {
    const d = new Date(dateStr);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const groupByMasa = (siparisler, odendi) => {
    const groups = {};
    siparisler.forEach(s => {
      const key = s.masaNumarasi || `Masa ${s.masaID || s.masaId || '?'}`;
      if (!groups[key]) groups[key] = { masaNumarasi: key, konum: s.konum || '', siparisler: [] };
      groups[key].siparisler.push(s);
    });
    return Object.values(groups).map(g => {
      const sorted = [...g.siparisler].sort((a, b) => new Date(b.siparisTarihi) - new Date(a.siparisTarihi));
      const ilk = sorted[sorted.length - 1];
      const son = sorted[0];
      let garsonAdi = '';
      for (const s of g.siparisler) { if (s.garsonAdi) { garsonAdi = s.garsonAdi; break; } }
      const toplamKalem = g.siparisler.reduce((t, s) => t + (s.urunSayisi || 0), 0);
      const toplamTutar = g.siparisler.reduce((t, s) => t + hesaplaToplam(s), 0);
      return {
        masaNumarasi: g.masaNumarasi, konum: g.konum,
        garsonAdi, ilkSiparisTarihi: ilk.siparisTarihi, sonSiparisTarihi: son.siparisTarihi,
        toplamKalem, toplamTutar, siparisSayisi: g.siparisler.length,
        sonSiparisID: son.siparisID, sonSiparis: son, siparisler: g.siparisler, odendi
      };
    }).sort((a, b) => (parseInt((a.masaNumarasi || '').replace(/\D/g, ''), 10) || 0) - (parseInt((b.masaNumarasi || '').replace(/\D/g, ''), 10) || 0));
  };

  const fetchSiparisler = async () => {
    try { const res = await fetch(`${API_URL}/siparisler`, { headers }); if (res.ok) setSiparisler(await res.json()); }
    catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const fetchOdemeler = async () => {
    try { const res = await fetch(`${API_URL}/odemeler`, { headers }); if (res.ok) { const d = await res.json(); setOdemeler(d); return d; } }
    catch (e) { console.error(e); }
    return odemeler;
  };

  const fetchUrunler = async () => {
    try { const res = await fetch(`${API_URL}/urunler`, { headers }); if (res.ok) setUrunler(await res.json()); }
    catch (e) { console.error(e); }
  };

  const fetchSiparisDetay = async (id) => {
    try { const res = await fetch(`${API_URL}/siparisler/${id}`, { headers }); if (res.ok) { const j = await res.json(); setSeciliDetay(j); } }
    catch (e) { console.error(e); }
  };

  const fetchGunlukOzet = async () => {
    try {
      const today = new Date();
      const tarih = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      const res = await fetch(`${API_URL}/raporlar/gunluk-ozet?tarih=${tarih}`, { headers });
      if (res.ok) setGunlukOzet(await res.json());
    } catch (e) { console.error(e); }
  };

  const fetchTahsilatData = async () => {
    setTahsilatYukleniyor(true);
    try {
      const res = await fetch(`${API_URL}/siparisler`, { headers });
      if (res.ok) setTahsilatSiparisler(await res.json());
      const res2 = await fetch(`${API_URL}/odemeler`, { headers });
      if (res2.ok) setTahsilatOdemeler(await res2.json());
    } catch (e) { console.error(e); } finally { setTahsilatYukleniyor(false); }
  };

  useEffect(() => {
    fetchSiparisler(); fetchOdemeler(); fetchUrunler(); fetchGunlukOzet();
    const interval = setInterval(() => {
      if (!modalAcik) { fetchSiparisler(); fetchOdemeler(); fetchUrunler(); fetchGunlukOzet(); }
    }, 30000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const connection = new HubConnectionBuilder()
      .withUrl('/hubs/siparis')
      .configureLogging(LogLevel.Information)
      .build();
    connection.on('YeniSiparisAlindi', () => { fetchSiparisler(); fetchOdemeler(); fetchUrunler(); fetchGunlukOzet(); });
    connection.on('SiparisGuncellendi', () => { fetchSiparisler(); fetchUrunler(); fetchGunlukOzet(); });
    connection.on('SiparisDurumuDegisti', () => { fetchSiparisler(); fetchUrunler(); fetchGunlukOzet(); });
    connection.on('SiparisKapatildi', () => { fetchSiparisler(); fetchOdemeler(); fetchUrunler(); fetchGunlukOzet(); });
    connection.start().catch(e => console.error('SignalR Error:', e));
    return () => { connection.stop(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (anaTab === 'tahsilatlar' && !tahsilatYuklendi.current) {
      fetchTahsilatData(); tahsilatYuklendi.current = true;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anaTab]);

  const todayStr = getTodayStr();
  const bugunSiparisler = siparisler.filter(s => toDateStr(s.siparisTarihi) === todayStr);
  // Açık masalar: tarih kısıtı olmadan tüm açık siparişler
  const tumAcik = siparisler.filter(s => s.siparisDurumu !== 'İptal' && s.siparisDurumu !== 'Tamamlandı' && s.odemeDurumu !== 'Ödendi' && s.odemeDurumu !== 'Odendi');
  const bugunOdenen = bugunSiparisler.filter(s => s.odemeDurumu === 'Ödendi' || s.odemeDurumu === 'Odendi');
  const bugunSiparisIds = new Set(bugunSiparisler.map(s => s.siparisID));
  const bugunOdemeleri = odemeler.filter(o => bugunSiparisIds.has(o.siparisID));
  const gunlukNakit = bugunOdemeleri.filter(o => (o.odemeTuru || '').toLowerCase().includes('nakit')).reduce((t, o) => t + (o.toplamTutar || o.odemeTutari || 0), 0);
  const gunlukKredi = bugunOdemeleri.filter(o => { const tur = (o.odemeTuru || '').toLowerCase(); return tur.includes('kredi') || tur.includes('kart'); }).reduce((t, o) => t + (o.toplamTutar || o.odemeTutari || 0), 0);
  const gunlukCiro = gunlukNakit + gunlukKredi;
  const acikMasaTutari = tumAcik.reduce((t, s) => t + hesaplaToplam(s), 0);
  const gunlukAcikMasalar = groupByMasa(tumAcik, false);
  const gunlukOdenenMasalar = groupByMasa(bugunOdenen, true);
  const gunlukGorunen = gunlukAltTab === 'acik' ? gunlukAcikMasalar : gunlukOdenenMasalar;

  const filtrelenmisT = tahsilatSiparisler.filter(s => {
    const tarih = toDateStr(s.siparisTarihi);
    if (baslangicTarih && tarih < baslangicTarih) return false;
    if (bitisTarih && tarih > bitisTarih) return false;
    if (selectedUrunId) {
      const urunVar = (s.urunIDList || []).some(id => String(id) === String(selectedUrunId));
      if (!urunVar) return false;
    }
    if (selectedKategoriId) {
      const katVar = (s.kategoriIDList || []).some(id => String(id) === String(selectedKategoriId));
      if (!katVar) return false;
    }
    return true;
  });
  const tahsilatAcik = filtrelenmisT.filter(s => s.siparisDurumu !== 'İptal' && s.siparisDurumu !== 'Tamamlandı' && s.odemeDurumu !== 'Ödendi' && s.odemeDurumu !== 'Odendi');
  const tahsilatOdenen = filtrelenmisT.filter(s => s.odemeDurumu === 'Ödendi' || s.odemeDurumu === 'Odendi');
  const filtrelenmisIds = new Set(filtrelenmisT.map(s => s.siparisID));
  const filtreliOdemeler = tahsilatOdemeler.filter(o => filtrelenmisIds.has(o.siparisID));
  const tahsilatNakit = filtreliOdemeler.filter(o => (o.odemeTuru || '').toLowerCase().includes('nakit')).reduce((t, o) => t + (o.toplamTutar || o.odemeTutari || 0), 0);
  const tahsilatKredi = filtreliOdemeler.filter(o => { const tur = (o.odemeTuru || '').toLowerCase(); return tur.includes('kredi') || tur.includes('kart'); }).reduce((t, o) => t + (o.toplamTutar || o.odemeTutari || 0), 0);
  const tahsilatCiro = tahsilatNakit + tahsilatKredi;
  const tahsilatAcikTutari = tahsilatAcik.reduce((t, s) => t + hesaplaToplam(s), 0);
  let tahsilatGorunen = tahsilatAltTab === 'acik' ? tahsilatAcik : tahsilatAltTab === 'odenen' ? tahsilatOdenen : [...tahsilatAcik, ...tahsilatOdenen].sort((a, b) => (b.siparisID || 0) - (a.siparisID || 0));

  const siparisDetayAc = (siparis) => {
    detayAc({
      siparisler: [siparis],
      sonSiparisID: siparis.siparisID,
      sonSiparis: siparis,
      masaNumarasi: siparis.masaNumarasi,
      garsonAdi: siparis.garsonAdi,
      konum: '',
      toplamTutar: hesaplaToplam(siparis),
      odendi: siparis.odemeDurumu === 'Ödendi' || siparis.odemeDurumu === 'Odendi',
      ilkSiparisTarihi: siparis.siparisTarihi,
      sonSiparisTarihi: siparis.siparisTarihi
    });
  };

  const detayAc = async (masaKart) => {
    setSeciliMasaKart(masaKart);
    setSeciliDetay(null);
    const ids = masaKart.siparisler.map(s => s.siparisID);
    if (ids.length <= 1) {
      fetchSiparisDetay(masaKart.sonSiparisID);
      return;
    }
    try {
      const sonuc = await Promise.all(ids.map(id =>
        fetch(`${API_URL}/siparisler/${id}`, { headers }).then(r => r.ok ? r.json() : null)
      ));
      const gecerli = sonuc.filter(r => r);
      if (gecerli.length > 0) {
        setSeciliDetay({
          ...gecerli[0],
          siparisDetaylari: gecerli.flatMap(r => r.siparisDetaylari || [])
        });
      }
    } catch (e) {
      console.error(e);
      fetchSiparisDetay(masaKart.sonSiparisID);
    }
  };

  const detayKapat = () => {
    setSeciliMasaKart(null);
    setSeciliDetay(null);
  };

  const urunIptalEt = async (detayId, siparisId) => {
    const result = await Swal.fire({
      title: 'Emin misiniz?', text: 'Bu ürünü iptal etmek istediğinize emin misiniz?', icon: 'warning',
      showCancelButton: true, confirmButtonText: 'Evet, İptal Et', cancelButtonText: 'Hayır, Vazgeç',
      confirmButtonColor: '#ef4444', cancelButtonColor: '#64748b'
    });
    if (!result.isConfirmed) return;
    try {
      const res = await fetch(`${API_URL}/siparisler/detay/${detayId}/durum`, { method: 'PATCH', headers, body: JSON.stringify('İptal') });
      if (res.ok) { fetchSiparisler(); if (siparisId) fetchSiparisDetay(siparisId); Swal.fire('İptal Edildi!', 'Ürün başarıyla iptal edildi.', 'success'); }
    } catch (e) { console.error(e); }
  };

  const fisYazdir = async (siparis, detay) => {
    try {
      const res = await fetch(`${API_URL}/siparisler/${siparis.siparisID}/adisyon-yazdir`, {
        method: 'POST', headers
      });
      if (res.ok) {
        const data = await res.json();
        Swal.fire({ title: 'Adisyon Gönderildi!', text: data.mesaj, icon: 'success', timer: 1500, showConfirmButton: false });
      } else {
        Swal.fire('Hata!', 'Yazdırma başarısız.', 'error');
      }
    } catch (e) {
      Swal.fire('Hata!', 'Bağlantı hatası.', 'error');
    }
  };

  const modalAc = (siparis) => { setModalSiparis(siparis); setTutarInput(String(kalanTutar(siparis))); setOdemeTuru('Nakit'); setModalAcik(true); };

  const odemeYap = async () => {
    if (!modalSiparis) return;
    const tutar = parseFloat(tutarInput.replace(',', '.'));
    if (isNaN(tutar) || tutar <= 0) { Swal.fire('Uyarı', 'Geçerli bir tutar girin!', 'warning'); return; }
    try {
      const res = await fetch(`${API_URL}/odemeler`, {
        method: 'POST', headers,
        body: JSON.stringify({ siparisID: modalSiparis.siparisID, odemeTuru, toplamTutar: tutar, kasaID: kullanici?.kullaniciID || 1, notlar: `${odemeTuru} odeme` })
      });
      if (res.ok) {
        const yeniOdemeler = await fetchOdemeler(); await fetchSiparisler();
        const siparisTutari = hesaplaToplam(modalSiparis);
        const odenen = yeniOdemeler.filter(o => o.siparisID === modalSiparis.siparisID).reduce((t, o) => t + (o.toplamTutar || o.odemeTutari || 0), 0);
        const kalan = Math.max(0, siparisTutari - odenen);
        if (kalan > 0) {
          Swal.fire('Kısmi Ödeme', `${fm(tutar)} alındı. Kalan: ${fm(kalan)}`, 'info');
          setTutarInput(String(kalan));
        } else {
          Swal.fire({ title: 'Ödeme Tamamlandı!', icon: 'success', timer: 1500, showConfirmButton: false });
          setModalAcik(false); detayKapat();
        }
      } else { Swal.fire('Hata!', 'Ödeme alınırken sunucu hatası oluştu!', 'error'); }
    } catch (e) { Swal.fire('Hata!', 'Ödeme işlemi sırasında bağlantı koptu!', 'error'); }
  };

  const modalAlmanUsuluAc = (siparis) => {
    setModalSiparis(siparis);
    const urunListesi = (seciliDetay?.siparisDetaylari || [])
      .filter(d => d.durum !== 'İptal' && (d.adet - (d.odenenAdet || 0)) > 0)
      .map(d => ({ ...d, kalanAdet: d.adet - (d.odenenAdet || 0), secilenAdet: 0 }));
    if (urunListesi.length === 0) { Swal.fire('Bilgi', 'Ödenecek ürün kalmamış!', 'info'); return; }
    setAlmanUsuluUrunler(urunListesi); setOdemeTuru('Nakit'); setAlmanUsuluAcik(true);
  };

  const almanUsuluAdetDegistir = (detayID, artis) => {
    setAlmanUsuluUrunler(prev => prev.map(u => {
      if (u.detayID === detayID) { const yeni = u.secilenAdet + artis; if (yeni >= 0 && yeni <= u.kalanAdet) return { ...u, secilenAdet: yeni }; }
      return u;
    }));
  };

  const almanUsuluOde = async () => {
    const seciliListe = almanUsuluUrunler.filter(u => u.secilenAdet > 0);
    if (seciliListe.length === 0) { Swal.fire('Uyarı', 'Lütfen ödenecek en az bir ürün seçin!', 'warning'); return; }
    const tutar = seciliListe.reduce((t, u) => t + ((u.birimFiyat || u.fiyat || 0) * u.secilenAdet), 0);
    try {
      const res = await fetch(`${API_URL}/odemeler/alman-usulu`, {
        method: 'POST', headers,
        body: JSON.stringify({
          siparisID: modalSiparis.siparisID, odemeTuru, toplamTutar: tutar,
          kasaID: kullanici?.kullaniciID || 1, notlar: `Alman Usulü - ${odemeTuru}`,
          detaylar: seciliListe.map(u => ({ detayID: u.detayID, adet: u.secilenAdet }))
        })
      });
      if (res.ok) {
        const yeniOdemeler = await fetchOdemeler(); await fetchSiparisler();
        if (seciliMasaKart) fetchSiparisDetay(modalSiparis.siparisID);
        const siparisTutari = hesaplaToplam(modalSiparis);
        const odenen = yeniOdemeler.filter(o => o.siparisID === modalSiparis.siparisID).reduce((t, o) => t + (o.toplamTutar || o.odemeTutari || 0), 0);
        const kalan = Math.max(0, siparisTutari - odenen);
        if (kalan > 0) {
          Swal.fire('Kısmi Ödeme (Alman Usulü)', `${fm(tutar)} tahsil edildi. Kalan: ${fm(kalan)}`, 'info');
        } else {
          Swal.fire({ title: 'Hesap Kapandı!', icon: 'success', timer: 1500, showConfirmButton: false });
          detayKapat();
        }
        setAlmanUsuluAcik(false); setModalSiparis(null);
      } else { Swal.fire('Hata!', 'Ödeme alınırken hata oluştu.', 'error'); }
    } catch (e) { Swal.fire('Hata', 'Sunucu ile iletişim koptu.', 'error'); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-50 dark:bg-surface-900 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-surface-400 font-medium">Yükleniyor...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-50 dark:bg-surface-900">
      <LockOverlay />
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white/80 dark:bg-surface-800/80 backdrop-blur-xl border-b border-surface-100 dark:border-surface-700/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <h1 className="font-display font-bold text-surface-800 dark:text-white text-sm">Kasa Paneli</h1>
            <div className="flex items-center gap-2">
              <button onClick={() => setAnaTab('gunluk')}
                className={`px-3.5 py-1.5 text-[11px] font-medium rounded-xl transition-all ${anaTab === 'gunluk' ? 'bg-emerald-500 text-white shadow-md' : 'bg-surface-100 dark:bg-surface-700 text-surface-500 dark:text-surface-400'}`}>
                Günlük
              </button>
              <button onClick={() => { setAnaTab('tahsilatlar'); detayKapat(); }}
                className={`px-3.5 py-1.5 text-[11px] font-medium rounded-xl transition-all ${anaTab === 'tahsilatlar' ? 'bg-emerald-500 text-white shadow-md' : 'bg-surface-100 dark:bg-surface-700 text-surface-500 dark:text-surface-400'}`}>
                Tahsilatlar
              </button>
              <button onClick={() => { logout(); navigate('/'); }}
                className="px-3.5 py-1.5 text-[11px] font-medium text-red-500 bg-red-50 dark:bg-red-900/20 rounded-xl hover:bg-red-100 dark:hover:bg-red-900/30 transition-all">
                Çıkış
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        {/* Ozet Kartlar */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
          {anaTab === 'gunluk' ? (
            <>
              <OzetCard label="Açık Masa" value={gunlukAcikMasalar.length ?? gunlukOzet?.aktifMasaSayisi} color="amber" icon="🪑" />
              <OzetCard label="Açık Tutar" value={fm(acikMasaTutari ?? gunlukOzet?.aktifMasaTutari)} color="amber" icon="💰" />
              <OzetCard label="Nakit" value={fm(gunlukNakit ?? gunlukOzet?.nakitToplam)} color="emerald" icon="💵" />
              <OzetCard label="Kredi/Kart" value={fm(gunlukKredi ?? gunlukOzet?.krediToplam)} color="blue" icon="💳" />
              <OzetCard label="Toplam Ciro" value={fm(gunlukCiro ?? gunlukOzet?.toplamCiro)} color="emerald" icon="📊" />
            </>
          ) : (
            <>
              <OzetCard label="Açık Sipariş" value={tahsilatAcik.length} color="amber" icon="🪑" />
              <OzetCard label="Açık Tutar" value={fm(tahsilatAcikTutari)} color="amber" icon="💰" />
              <OzetCard label="Nakit" value={fm(tahsilatNakit)} color="emerald" icon="💵" />
              <OzetCard label="Kredi/Kart" value={fm(tahsilatKredi)} color="blue" icon="💳" />
              <OzetCard label="Toplam Ciro" value={fm(tahsilatCiro)} color="emerald" icon="📊" />
            </>
          )}
        </div>

        {/* Tab 1: Günlük */}
        {anaTab === 'gunluk' && (
          <>
            <div className="flex gap-2 mb-4">
              <button onClick={() => setGunlukAltTab('acik')}
                className={`px-4 py-2 text-xs font-semibold rounded-xl transition-all ${gunlukAltTab === 'acik' ? 'bg-emerald-500 text-white shadow-md' : 'bg-white dark:bg-surface-800 text-surface-500 dark:text-surface-400 border border-surface-200 dark:border-surface-700'}`}>
                Açık ({gunlukOzet?.aktifMasaSayisi ?? gunlukAcikMasalar.length})
              </button>
              <button onClick={() => setGunlukAltTab('odenen')}
                className={`px-4 py-2 text-xs font-semibold rounded-xl transition-all ${gunlukAltTab === 'odenen' ? 'bg-emerald-500 text-white shadow-md' : 'bg-white dark:bg-surface-800 text-surface-500 dark:text-surface-400 border border-surface-200 dark:border-surface-700'}`}>
                Ödenen ({gunlukOdenenMasalar.length})
              </button>
            </div>
            {gunlukGorunen.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-surface-300 dark:text-surface-600">
                <svg className="w-12 h-12 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-xs font-medium">Bu kategoride masa bulunmuyor</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {gunlukGorunen.map(mk => (
                  <MasaKartKasa key={mk.masaNumarasi} masaKart={mk} detayAc={detayAc} />
                ))}
              </div>
            )}
          </>
        )}

        {/* Tab 2: Tahsilatlar */}
        {anaTab === 'tahsilatlar' && (
          <>
            <div className="bg-white dark:bg-surface-800 rounded-2xl p-4 border border-surface-100 dark:border-surface-700 shadow-soft mb-4">
              <div className="flex flex-wrap gap-3 items-end">
                <div>
                  <label className="block text-[10px] font-medium text-surface-400 mb-1">Başlangıç</label>
                  <input type="date" value={baslangicTarih} onChange={e => setBaslangicTarih(e.target.value)}
                    className="text-xs bg-surface-50 dark:bg-surface-700 border border-surface-200 dark:border-surface-600 rounded-xl px-3 py-2 text-surface-700 dark:text-surface-300 focus:outline-none focus:ring-2 focus:ring-emerald-500/20" />
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-surface-400 mb-1">Bitiş</label>
                  <input type="date" value={bitisTarih} onChange={e => setBitisTarih(e.target.value)}
                    className="text-xs bg-surface-50 dark:bg-surface-700 border border-surface-200 dark:border-surface-600 rounded-xl px-3 py-2 text-surface-700 dark:text-surface-300 focus:outline-none focus:ring-2 focus:ring-emerald-500/20" />
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-surface-400 mb-1">Ürün Grubu</label>
                  <select value={selectedKategoriId} onChange={e => setSelectedKategoriId(e.target.value)}
                    className="text-xs bg-surface-50 dark:bg-surface-700 border border-surface-200 dark:border-surface-600 rounded-xl px-3 py-2 text-surface-700 dark:text-surface-300 focus:outline-none focus:ring-2 focus:ring-emerald-500/20">
                    <option value="">Tüm Gruplar</option>
                    {kategoriler.map(k => <option key={k.kategoriID} value={k.kategoriID}>{k.kategoriAdi}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-surface-400 mb-1">Ürün</label>
                  <select value={selectedUrunId} onChange={e => setSelectedUrunId(e.target.value)}
                    className="text-xs bg-surface-50 dark:bg-surface-700 border border-surface-200 dark:border-surface-600 rounded-xl px-3 py-2 text-surface-700 dark:text-surface-300 focus:outline-none focus:ring-2 focus:ring-emerald-500/20">
                    <option value="">Tüm Ürünler</option>
                    {urunler.map(u => <option key={u.urunID} value={u.urunID}>{u.urunAdi}</option>)}
                  </select>
                </div>
                <button onClick={fetchTahsilatData} disabled={tahsilatYukleniyor}
                  className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold rounded-xl shadow-lg shadow-emerald-500/20 transition-all disabled:opacity-50">
                  {tahsilatYukleniyor ? 'Yükleniyor...' : 'Verileri Yenile'}
                </button>
              </div>
            </div>

            <div className="flex gap-2 mb-4">
              {[
                { key: 'tumu', label: 'Tümü', count: filtrelenmisT.length },
                { key: 'acik', label: 'Açık', count: tahsilatAcik.length },
                { key: 'odenen', label: 'Ödenen', count: tahsilatOdenen.length },
              ].map(t => (
                <button key={t.key} onClick={() => setTahsilatAltTab(t.key)}
                  className={`px-4 py-2 text-xs font-semibold rounded-xl transition-all ${tahsilatAltTab === t.key ? 'bg-emerald-500 text-white shadow-md' : 'bg-white dark:bg-surface-800 text-surface-500 dark:text-surface-400 border border-surface-200 dark:border-surface-700'}`}>
                  {t.label} ({t.count})
                </button>
              ))}
            </div>

            {tahsilatGorunen.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-surface-300 dark:text-surface-600">
                <svg className="w-12 h-12 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-xs font-medium">
                  {tahsilatSiparisler.length === 0 ? 'Verileri görmek için "Verileri Yenile" butonuna tıklayın.' : 'Bu kriterlere uygun sipariş bulunmuyor.'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {tahsilatGorunen.map(s => {
                  const odendi = s.odemeDurumu === 'Ödendi' || s.odemeDurumu === 'Odendi';
                  const sure = sureFarki(s.siparisTarihi);
                  return (
                    <div key={s.siparisID}
                      className={`relative rounded-2xl border cursor-pointer transition-all duration-300 overflow-hidden group hover:shadow-xl hover:-translate-y-0.5 ${odendi ? 'border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-900/10 shadow-lg shadow-emerald-500/5' : 'border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/10 shadow-lg shadow-amber-500/5'}`}
                      onClick={() => siparisDetayAc(s)}>
                      <div className={`absolute left-0 top-0 bottom-0 w-1.5 rounded-l-2xl ${odendi ? 'bg-emerald-400' : 'bg-amber-400'}`} />
                      <div className="p-4 pl-5">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h3 className="font-bold text-surface-800 dark:text-white text-base">{s.masaNumarasi}</h3>
                            <p className="text-[10px] text-surface-400 font-mono">#{s.siparisID}</p>
                          </div>
                          {odendi ? (
                            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                              Ödendi
                            </div>
                          ) : (
                            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold tabular-nums bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                              {sure}
                            </div>
                          )}
                        </div>
                        <div className="space-y-1.5 text-xs">
                          <div className="flex items-center justify-between">
                            <span className="text-surface-400">Garson</span>
                            <span className="font-semibold text-surface-700 dark:text-surface-200">{s.garsonAdi || '?'}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-surface-400">Tarih</span>
                            <span className="font-medium text-surface-700 dark:text-surface-200">{formatDate(s.siparisTarihi)}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-surface-400">Saat</span>
                            <span className="font-medium text-surface-700 dark:text-surface-200">{formatSaat(s.siparisTarihi)}</span>
                          </div>
                          <div className="border-t border-surface-100 dark:border-surface-700/50 my-1.5" />
                          <div className="flex items-center justify-between">
                            <span className="text-surface-400">Tutar</span>
                            <span className="font-bold text-emerald-600 dark:text-emerald-400">{fm(hesaplaToplam(s))}</span>
                          </div>
                        </div>
                      </div>
                      <div className="px-4 pb-4 pl-5">
                        <div className="w-full py-2 text-center text-xs font-semibold bg-white/80 dark:bg-surface-700/80 backdrop-blur-sm border border-surface-200 dark:border-surface-600 text-surface-600 dark:text-surface-300 rounded-xl hover:bg-white dark:hover:bg-surface-600 transition-all opacity-80 group-hover:opacity-100">
                          {odendi ? 'Fişleri Görüntüle' : 'Sipariş Detayı'}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      {/* Masa Detay Modal */}
      {seciliMasaKart && (
        <MasaDetayModal
          masaKart={seciliMasaKart} seciliDetay={seciliDetay}
          onClose={detayKapat} odemeler={odemeler} kullanici={kullanici}
          hesaplaToplam={hesaplaToplam} masaKalanTutar={masaKalanTutar}
          urunIptalEt={urunIptalEt} modalAc={modalAc}
          modalAlmanUsuluAc={modalAlmanUsuluAc} fisYazdir={fisYazdir} />
      )}

      {/* Normal Ödeme Modal */}
      {modalAcik && modalSiparis && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setModalAcik(false)}>
          <div className="bg-white dark:bg-surface-800 rounded-3xl w-full max-w-md shadow-soft-xl animate-scale-in" onClick={e => e.stopPropagation()}>
            <div className="p-6">
              <h3 className="font-display font-bold text-surface-800 dark:text-white text-base mb-5">
                Hesap Al — {modalSiparis.masaNumarasi || `Masa ${modalSiparis.masaId || modalSiparis.masaID}`}
              </h3>
              <div className="mb-4">
                <label className="block text-[11px] font-medium text-surface-400 mb-1.5">Tutar (TL)</label>
                <input type="number" step="0.01" value={tutarInput} onChange={e => setTutarInput(e.target.value)}
                  className="w-full text-lg bg-surface-50 dark:bg-surface-700 border border-surface-200 dark:border-surface-600 rounded-2xl px-4 py-3 text-surface-800 dark:text-white font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500/20" />
              </div>
              <div className="mb-5">
                <label className="block text-[11px] font-medium text-surface-400 mb-1.5">Ödeme Yöntemi</label>
                <div className="grid grid-cols-2 gap-2">
                  {['Nakit', 'Kredi Kartı', 'Debit Kart', 'Havale'].map(y => (
                    <button key={y} onClick={() => setOdemeTuru(y)}
                      className={`py-2.5 text-xs font-semibold rounded-xl transition-all ${
                        odemeTuru === y ? 'bg-emerald-500 text-white shadow-md' : 'bg-surface-50 dark:bg-surface-700 text-surface-500 dark:text-surface-400 border border-surface-200 dark:border-surface-600 hover:bg-surface-100 dark:hover:bg-surface-600'
                      }`}>
                      {y}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setModalAcik(false)}
                  className="flex-1 py-3 text-xs font-semibold bg-surface-100 dark:bg-surface-700 text-surface-500 dark:text-surface-400 rounded-xl hover:bg-surface-200 dark:hover:bg-surface-600 transition-all">
                  İptal
                </button>
                <button onClick={odemeYap}
                  className="flex-1 py-3 text-xs font-semibold bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl shadow-lg shadow-emerald-500/20 transition-all active:scale-[0.98]">
                  {fm(parseFloat(tutarInput || 0))} Al
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Alman Usulü Modal */}
      {almanUsuluAcik && modalSiparis && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setAlmanUsuluAcik(false)}>
          <div className="bg-white dark:bg-surface-800 rounded-3xl w-full max-w-lg shadow-soft-xl animate-scale-in" onClick={e => e.stopPropagation()}>
            <div className="p-6">
              <h3 className="font-display font-bold text-surface-800 dark:text-white text-base mb-1">
                Alman Usulü — {modalSiparis.masaNumarasi || `Masa ${modalSiparis.masaId || modalSiparis.masaID}`}
              </h3>
              <p className="text-[11px] text-surface-400 mb-4">Müşterinin ödeyeceği ürünleri ve adetleri seçiniz.</p>

              <div className="max-h-60 overflow-y-auto space-y-1.5 mb-4">
                {almanUsuluUrunler.map(u => (
                  <div key={u.detayID} className="flex items-center justify-between p-3 rounded-xl bg-surface-50 dark:bg-surface-700/30">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-surface-800 dark:text-surface-200 text-xs truncate">{u.urunAdi}</p>
                      <p className="text-[10px] text-surface-400">
                        Birim: {fm(u.birimFiyat || u.fiyat || 0)} · Kalan: {u.kalanAdet}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => almanUsuluAdetDegistir(u.detayID, -1)} disabled={u.secilenAdet === 0}
                        className="w-7 h-7 rounded-lg bg-white dark:bg-surface-700 border border-surface-200 dark:border-surface-600 flex items-center justify-center text-surface-500 hover:bg-surface-50 transition-colors disabled:opacity-30">
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" /></svg>
                      </button>
                      <span className="w-5 text-center text-xs font-bold text-surface-800 dark:text-white">{u.secilenAdet}</span>
                      <button onClick={() => almanUsuluAdetDegistir(u.detayID, 1)} disabled={u.secilenAdet >= u.kalanAdet}
                        className="w-7 h-7 rounded-lg bg-white dark:bg-surface-700 border border-surface-200 dark:border-surface-600 flex items-center justify-center text-surface-500 hover:bg-surface-50 transition-colors disabled:opacity-30">
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-between mb-4 px-1">
                <span className="text-sm font-semibold text-surface-500 dark:text-surface-400">Seçilen Toplam</span>
                <span className="font-display font-bold text-lg text-cyan-600 dark:text-cyan-400">
                  {fm(almanUsuluUrunler.reduce((t, u) => t + ((u.birimFiyat || u.fiyat || 0) * u.secilenAdet), 0))}
                </span>
              </div>

              <div className="mb-4">
                <label className="block text-[11px] font-medium text-surface-400 mb-1.5">Ödeme Yöntemi</label>
                <div className="grid grid-cols-2 gap-2">
                  {['Nakit', 'Kredi Kartı', 'Debit Kart', 'Havale'].map(y => (
                    <button key={y} onClick={() => setOdemeTuru(y)}
                      className={`py-2.5 text-xs font-semibold rounded-xl transition-all ${
                        odemeTuru === y ? 'bg-emerald-500 text-white shadow-md' : 'bg-surface-50 dark:bg-surface-700 text-surface-500 dark:text-surface-400 border border-surface-200 dark:border-surface-600'
                      }`}>
                      {y}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex gap-2">
                <button onClick={() => setAlmanUsuluAcik(false)}
                  className="flex-1 py-3 text-xs font-semibold bg-surface-100 dark:bg-surface-700 text-surface-500 dark:text-surface-400 rounded-xl hover:bg-surface-200 dark:hover:bg-surface-600 transition-all">
                  İptal
                </button>
                <button onClick={almanUsuluOde}
                  className="flex-1 py-3 text-xs font-semibold bg-cyan-500 hover:bg-cyan-600 text-white rounded-xl shadow-lg shadow-cyan-500/20 transition-all active:scale-[0.98]">
                  Hesabı Al
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KasaPanel;
